import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  ripple: boolean;
  menuMode: string;
  scale: number;
}

export interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
}

const DEFAULT_CONFIG: AppConfig = {
  ripple: false,
  inputStyle: 'outlined',
  menuMode: 'static',
  colorScheme: 'light',
  theme: 'saga-purple',
  scale: 14
};

const DEFAULT_STATE: LayoutState = {
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false
};

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private readonly _config = signal<AppConfig>({ ...DEFAULT_CONFIG });
  private readonly _state = signal<LayoutState>({ ...DEFAULT_STATE });

  readonly config = this._config.asReadonly();
  readonly state = this._state.asReadonly();

  private readonly overlayOpen = new Subject<void>();
  readonly overlayOpen$ = this.overlayOpen.asObservable();

  setConfig(config: AppConfig): void {
    this._config.set(config);
  }

  updateState(partial: Partial<LayoutState>): void {
    this._state.update((state) => ({ ...state, ...partial }));
  }

  resetMenuState(): void {
    this.updateState({
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false
    });
  }

  hideProfileSidebar(): void {
    this.updateState({ profileSidebarVisible: false });
  }

  onMenuToggle(): void {
    if (this.isOverlay()) {
      const overlayMenuActive = !this._state().overlayMenuActive;
      this.updateState({ overlayMenuActive });
      if (overlayMenuActive) {
        this.overlayOpen.next();
      }
    }

    if (this.isDesktop()) {
      this.updateState({
        staticMenuDesktopInactive: !this._state().staticMenuDesktopInactive
      });
    } else {
      const staticMenuMobileActive = !this._state().staticMenuMobileActive;
      this.updateState({ staticMenuMobileActive });
      if (staticMenuMobileActive) {
        this.overlayOpen.next();
      }
    }
  }

  showProfileSidebar(): void {
    const profileSidebarVisible = !this._state().profileSidebarVisible;
    this.updateState({ profileSidebarVisible });
    if (profileSidebarVisible) {
      this.overlayOpen.next();
    }
  }

  showConfigSidebar(): void {
    this.updateState({ configSidebarVisible: true });
  }

  isOverlay(): boolean {
    return this._config().menuMode === 'overlay';
  }

  isDesktop(): boolean {
    return window.innerWidth > 991;
  }

  isMobile(): boolean {
    return !this.isDesktop();
  }
}
